import { noItx } from "../translationItx/itx";

export enum EnumLightTypes {
  dimmable = "dimmable", // no itx
  switchable = "switchable", // no itx
  tunableWhite = "tunable white", // no itx
  rgb = "rgb", // no itx
  rgbw = "rgbw", // no itx
}

export enum EnumFunctionTypes {
  heating = "heating", // no itx
  jalousie = "jalousie", // no itx
  blind = "blind", // no itx
  sockets = "sockets", // no itx
  switchableSockets = "switchable sockets", // no itx
  windowContact = "window contact", // no itx
}

export type EnumChannelTypes = EnumLightTypes | EnumFunctionTypes;

type ParameterDefObj = {
  type: string;
  description: string;
  enum?: Array<string>;
};
export default class DefGPTParameter {
  //////////////////////////////
  /// different names
  /////////////////////////////
  static get objName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The name of the object."),
    };
  }

  static get objId(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The id of the object. Has the form of a GUID."),
    };
  }

  static get cabinetName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        `The cabinet where the actor should be created. 
Examples: 'cabinet ground floor', 'cabinet attic', 'cabinet upper floor', 'cabinet 2nd floor', 'main cabinet'
It is importent to use for the cabinet name the original name of the cabinet 
as it is used in the project or given by the user without any translation
`,
      ),
    };
  }

  static get actorName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The actor name. Examples: A1, D1, S2, J2, 'actor 1', 'switch actor', 'dimmer actor'"),
    };
  }

  static get manufacturerName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The manufacturer of the product."),
    };
  }

  static get objectName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(`The name of an object.`),
    };
  }

  static get floorName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        `The name of a floor. 
It is importent to use the original name of the floor 
as it is used in the project or given by the user without any translation
`,
      ),
      enum: [""], // enums are set in "init"
    };
  }

  static get roomName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        `The name of a room. 
Examples: 'Living room', 'Kitchen', 'Bedroom', 'Bathroom', 'Office', 'Hallway', 'Dining room'
It is importent to use the original name of the room, 
as it is used in the project or given by the user, 
without any translation
`,
      ),
    };
  }

  static get lightName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        `The light name. 
Examples: 'Ceiling light', 'Wall light', 'Floor lamp', 'Downlight', 'LED strip', 'Pendant light'
It is importent to use for the light name the original name of the light 
as it is used in the project or given by the user without any translation`,
      ),
    };
  }

  static get functionName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The function name. Examples: 'Window', 'Door', 'Heating', 'Ventilation', 'Shutter'"),
    };
  }

  static get partnerName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        "The name of the partner object to be connected. The partner can be a light, function, or scene.",
      ),
    };
  }

  static get sceneName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The scene name. Examples: 'Living', 'Sleeping', 'Cooking', 'Reading', 'Party', 'Away'"),
    };
  }

  static get panelName(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The panel name or tag. Examples: T1, T2, 'Entrance', 'Door', 'Stairs', or a room name."),
    };
  }

  static get fullTag(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        `The tag of the object. 
The tag is used to identify the object in the system. 
It is a unique, human-readable identifier for the object.`,
      ),
    };
  }
  //////////////////////////////
  /// different objects
  /////////////////////////////

  static get parentObj(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The name or the fullTag of the parent of the object in the hierachy"),
    };
  }

  //////////////////////////////
  /// different types
  /////////////////////////////

  static get lightType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The type of the light"),
      enum: [
        "dimmable", // no itx
        "switchable", // no itx
        "tunable white", // no itx
        "rgb", // no itx
        "rgbw", // no itx
      ],
    };
  }

  static get objectNumber(): ParameterDefObj {
    return {
      type: "number",
      description: noItx("The number of objects to create. This is used to create multiple objects of the same type."),
    };
  }

  static get functionType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The type of the function"),

      enum: [
        EnumFunctionTypes.heating,
        EnumFunctionTypes.jalousie,
        EnumFunctionTypes.blind,
        EnumFunctionTypes.sockets,
        EnumFunctionTypes.switchableSockets,
        EnumFunctionTypes.windowContact,
      ],
    };
  }
  static get partnerButtonType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The type of the partner object that should be connected.d"),
      enum: ["light", "function", "scene"], // no itx
    };
  }
  static get objectTypeRoom(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The type of the searched objects"),
      enum: ["light", "heating", "jalousie", "blind", "panel"], // no itx
    };
  }

  static get objectType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The type of the object"),
      enum: [
        "floor", // no itx
        "room", // no itx
        "light", // no itx
        "function", // no itx
        "object set", // no itx
        "panel", // no itx
        "cabinet", // no itx
        "actor", // no itx
        "scene", // no itx
        "fuse", // no itx
        "elcb", // no itx
        "terminal", // no itx
        "terminal strip", // no itx
      ],
    };
  }

  static get productType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx(
        "The Type of a  product. ig. dimm actor, switch actor, heating actor, jalousie actor, blind actor, Schaltaktor, Dimmaktor, Heizaktor, Jalousieaktor, Rollandenakor",
      ),
    };
  }
  //////////////////////////////
  /// different Id
  /////////////////////////////
  static get orderId(): ParameterDefObj {
    return {
      type: "string",
      // `The order id or order number of the product i.e.AKK-0416.03, AKS-0416.03.
      description: `Die Bestellnummer des Produkts. zB. AKK-0416.03, AKS-0416.03, AKK.xxxx, JAL-0810M.02, SCN-DA642, AKU-02UP.03. 
Die Bestellnummer beschreibt zusammen mit dem Hersteller eine Produkt und wird für die genaue Identifikation des Produkts gebraucht.
Die Bestellnummer wird auch Artikelnummer oder Artikel genannt.
Die Bestellnummer hat ein unterschiedliches Format je nach Hersteller und ist eine Abfolge von Buchstabe, Zahlen und Sonderzeichen.
`,
    };
  }
  static get EAN(): ParameterDefObj {
    return {
      type: "string",
      // `The order id or order number of the product i.e.AKK-0416.03, AKS-0416.03.
      description: `Eine EAN-Nummer, kurz für European Article Number, ist eine eindeutige Artikelnummer, die zur Identifizierung von Produkten im Handel verwendet wird.
      es ist eine 13-stellige Nummer.
        `,
    };
  }

  static get idType(): ParameterDefObj {
    return {
      type: "string",
      description: noItx("The unique system identifier of an object"),
    };
  }

  //////////////////////////////
  /// special
  /////////////////////////////
  static get buttonPosition(): ParameterDefObj {
    return {
      type: "string",
      description: `The position of the Button of the panel. e.g. top left, top , middle, 1. button, 3. button, last button, top left, top right, middle left, middle right, bottom left, bottom right, patsch.
          If no specification of the position of the Button is given, use 'unkown'
          `,

      enum: [
        noItx("left"),
        "right",
        noItx("top"),
        "middle",
        "bottom",
        noItx("first button"),
        noItx("1. button"),
        noItx("2. button"),
        noItx("3. button"),
        noItx("4. button"),
        noItx("last button"),
        noItx("top left"),
        noItx("top right"),
        noItx("middle left"),
        noItx("middle right"),
        noItx("bottom left"),
        noItx("bottom right"),
        noItx("patsch"),
        noItx("unkown"),
      ],
    };
  }
  static get longPress(): ParameterDefObj {
    return {
      type: "boolean",
      description: noItx(
        "'Longpress' or 'press long' is a sub function of the Button. This sub function is activated by pressing the button for al long time i.e. for 1 second",
      ),
    };
  }
}
